import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiFileText, FiTrendingUp, FiBookOpen, FiBriefcase, FiDollarSign, FiShield } from 'react-icons/fi';
import { useTranslation } from '../hooks/useTranslation';

const ServicesSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const { t } = useTranslation();

  const services = [
    {
      icon: <FiFileText size={32} />,
      title: 'Tax Preparation',
      description: 'Comprehensive tax filing and planning services for individuals and businesses.',
      features: ['Individual Tax Returns', 'Corporate Tax Filing', 'Tax Planning', 'IRS Representation'],
      color: 'from-[#A37A00] to-[#8A6800]'
    },
    {
      icon: <FiBookOpen size={32} />,
      title: 'Bookkeeping',
      description: 'Accurate financial record keeping and management.',
      features: ['Monthly Reports', 'Financial Statements', 'Accounts Management', 'Bank Reconciliation'],
      color: 'from-amber-600 to-amber-800'
    },
    {
      icon: <FiTrendingUp size={32} />,
      title: 'Financial Planning',
      description: 'Strategic financial guidance for long-term growth and stability.',
      features: ['Investment Planning', 'Retirement Planning', 'Wealth Management', 'Risk Assessment'],
      color: 'from-amber-700 to-amber-900'
    },
    {
      icon: <FiBriefcase size={32} />,
      title: 'Business Consulting',
      description: 'Expert advice for business growth and operational efficiency.',
      features: ['Business Strategy', 'Cost Reduction', 'Process Optimization', 'Growth Planning'],
      color: 'from-amber-800 to-amber-950'
    },
    {
      icon: <FiDollarSign size={32} />,
      title: 'Payroll Services',
      description: 'Efficient payroll processing and compliance management.',
      features: ['Payroll Processing', 'Tax Withholding', 'Benefits Administration', 'Compliance'],
      color: 'from-[#A37A00] to-amber-800'
    },
    {
      icon: <FiShield size={32} />,
      title: 'Audit & Assurance',
      description: 'Reliable audit services for financial transparency and compliance.',
      features: ['Internal Audits', 'Financial Reviews', 'Compliance Audits', 'Risk Management'],
      color: 'from-amber-800 to-[#8A6800]'
    }
  ];

  return (
    <section id="services" className="py-20 relative">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[#A37A00] to-[#8A6800] bg-clip-text text-transparent">
              {t('services.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        <div ref={ref} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              
              <div className="relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl p-6 h-full border border-gray-200 dark:border-gray-700 group-hover:border-amber-500/30 transition-all duration-300">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${service.color} mb-6`}>
                  <div className="text-white">
                    {service.icon}
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#A37A00] mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ x: 5 }}
                  className="flex items-center text-[#A37A00] dark:text-amber-400 font-semibold"
                >
                  {t('services.learnMore')}
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                  </svg>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-gradient-to-r from-[#A37A00] to-[#8A6800] text-white rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow"
          >
            {t('services.viewAll')}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;